<script setup>
import { computed, ref, onUnmounted, watch } from 'vue';

const props = defineProps({
  showModal: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
  initialStep: {
    type: Number,
    default: 1,
  },
  lastStep: {
    type: Number,
    default: 1,
  },
  startButtonText: {
    type: String,
    default: '',
  },
  hideFinalButton: {
    type: Boolean,
    default: false,
  },
  width: {
    type: Number,
    default: 600,
  },
});

const emit = defineEmits(['close', 'next', 'back', 'opened']);

const currentStep = ref(props.initialStep);
const isInitialStep = computed(() => currentStep.value === props.initialStep);
let timerId = null;

const handleClose = (event) => {
  emit('close', { event, currentStep: currentStep.value });
};

const handleNextButton = () => {
  const nextStep = currentStep.value + 1;
  emit('next', { currentStep: currentStep.value, nextStep });
  currentStep.value = nextStep;
};

const handleBackButton = () => {
  const nextStep = currentStep.value - 1;
  emit('back', { currentStep: currentStep.value, nextStep });
  currentStep.value = nextStep;
};

const handleOpened = () => {
  currentStep.value = props.initialStep;
  emit('opened');
};

watch(
  () => props.showModal,
  (newValue, oldValue) => {
    const closedModal = oldValue === true && newValue === false;
    if (closedModal) {
      timerId = setTimeout(() => {
        currentStep.value = -1;
      }, 200);
    }
  }
);

onUnmounted(() => {
  if (timerId) {
    clearTimeout(timerId);
  }
});
</script>

<template>
  <XModal
    :is-open="showModal"
    :title="title"
    icon-close
    data-testid="multiStepModal"
    :width="width"
    @opened="handleOpened"
    @close="handleClose"
  >
    <slot
      name="body"
      :current-step="currentStep"
    />

    <template #actions>
      <div :class="$style.footer">
        <span
          v-if="currentStep > 0"
          :class="$style.stepper"
          data-testid="modalStepper"
        >
          {{ $t('general.stepper', [currentStep, lastStep]) }}
        </span>
        <div :class="$style.buttons">
          <XButton
            v-if="currentStep > initialStep"
            design="outlined"
            data-testid="modalBackStepButton"
            @click="handleBackButton"
          >
            {{ $t('general.action.back') }}
          </XButton>

          <XButton
            v-if="currentStep < lastStep"
            data-testid="modalNextStepButton"
            @click="handleNextButton"
          >
            {{ isInitialStep && startButtonText ? startButtonText : $t('general.action.next') }}
          </XButton>

          <XButton
            v-else-if="currentStep === lastStep && !hideFinalButton"
            data-testid="modalFinalButton"
            @click="handleClose"
          >
            {{ $t('general.action.close') }}
          </XButton>
        </div>
      </div>
    </template>
  </XModal>
</template>

<style lang="scss" module>
.footer {
  display: flex;
  align-items: center;
  width: 100%;

  .stepper {
    color: var(--color-neutral-700);
  }

  .buttons {
    display: flex;
    align-items: center;
    margin-left: auto;
    gap: var(--space-small-xx);
  }
}
</style>
